/**
 * Mock data for local backend testing
 */

import type { ExternalData } from "@easyblocks/core";
import type {
  MediaUploadResponse,
  PrivateDocCategoriesResponse,
  PrivateDocListResponse,
  PrivateDocUploadResponse,
} from "../types";
import { fileToDataUrl } from "../media";

/**
 * TESTING FLAGS
 * Set these to true to test "no data" scenarios without removing mock data
 */
export const MOCK_EMPTY_NEWS = false;
export const MOCK_EMPTY_EVENTS = false;
export const MOCK_EMPTY_ROSTER = false;
export const MOCK_EMPTY_DOCUMENTS = false;
export const MOCK_EMPTY_ALBUMS = false;

/**
 * Mock news data for local backend
 */
export const mockNewsData: Record<string, any> = {
  "news:1": {
    item_id: 1,
    item_name: "Local News #1 (mock)",
    date_posted: "2025-01-01",
    slug: "local-news-1",
    teaser: "This is a mocked teaser for local testing.",
    content: "This is mocked content.\n\nNo HTML rendering by default.",
    page_title: "Local News #1",
    meta_keywords: "local,news,mock",
    meta_description: "Mock news item for local backend testing.",
  },
  "news:2": {
    item_id: 2,
    item_name: "Local News #2 (mock)",
    date_posted: "2025-02-15",
    slug: "local-news-2",
    teaser: "Another mocked teaser.",
    content: "Second mocked content body.",
    page_title: "Local News #2",
    meta_keywords: "local,news,mock,second",
    meta_description: "Second mock news item.",
  },
  "news:3": {
    item_id: 3,
    item_name: "Local News #3 (mock)",
    date_posted: "2025-03-10",
    slug: "local-news-3",
    teaser: "Mock teaser for the third item (latest).",
    content: "Third mocked content body.",
    page_title: "Local News #3",
    meta_keywords: "local,news,mock,third",
    meta_description: "Third mock news item.",
  },
  "news:4": {
    item_id: 4,
    item_name: "Community Spotlight: Local Heroes (mock)",
    date_posted: "2025-04-05",
    slug: "community-spotlight-local-heroes",
    teaser: "Meet the volunteers making a difference in our community.",
    content: "Our community is full of amazing people doing incredible work. This month we spotlight three local heroes who have dedicated their time to making our neighborhood a better place.",
    page_title: "Community Spotlight: Local Heroes",
    meta_keywords: "community,volunteers,local,heroes",
    meta_description: "Highlighting local volunteers making a difference.",
  },
  "news:5": {
    item_id: 5,
    item_name: "Annual Fundraiser Exceeds Goals (mock)",
    date_posted: "2025-05-12",
    slug: "annual-fundraiser-exceeds-goals",
    teaser: "Thanks to your generous support, we've exceeded our fundraising goals!",
    content: "We're thrilled to announce that our annual fundraiser has exceeded all expectations. With your help, we raised over $50,000 to support our programs and initiatives.",
    page_title: "Annual Fundraiser Exceeds Goals",
    meta_keywords: "fundraiser,success,goals,community",
    meta_description: "Annual fundraiser surpasses expectations.",
  },
  "news:6": {
    item_id: 6,
    item_name: "New Programs Launching This Fall (mock)",
    date_posted: "2025-06-20",
    slug: "new-programs-launching-fall",
    teaser: "Exciting new programs are coming this fall. Registration opens soon!",
    content: "We're excited to announce several new programs launching this fall, including youth sports leagues, art classes, and community workshops. Stay tuned for registration details.",
    page_title: "New Programs Launching This Fall",
    meta_keywords: "programs,fall,registration,youth",
    meta_description: "New fall programs announced.",
  },
  "news:7": {
    item_id: 7,
    item_name: "Facility Upgrades Complete (mock)",
    date_posted: "2025-07-18",
    slug: "facility-upgrades-complete",
    teaser: "Our facility upgrades are complete! Come see the improvements.",
    content: "After months of work, our facility upgrades are complete. New features include updated meeting rooms, improved accessibility, and modern technology throughout the building.",
    page_title: "Facility Upgrades Complete",
    meta_keywords: "facility,upgrades,improvements,accessibility",
    meta_description: "Facility renovation project completed.",
  },
  "news:8": {
    item_id: 8,
    item_name: "Member Appreciation Event Announced (mock)",
    date_posted: "2025-08-25",
    slug: "member-appreciation-event",
    teaser: "Join us for our annual member appreciation event on September 15th!",
    content: "Mark your calendars! Our annual member appreciation event is scheduled for September 15th. Enjoy food, entertainment, and special recognition for our long-time members.",
    page_title: "Member Appreciation Event Announced",
    meta_keywords: "member,appreciation,event,celebration",
    meta_description: "Annual member appreciation event details.",
  },
};

/**
 * Mock news search items
 */
export const mockNewsSearchItems = [
  { id: "news:8", name: "Member Appreciation Event Announced (mock)", date_posted: "2025-08-25" },
  { id: "news:7", name: "Facility Upgrades Complete (mock)", date_posted: "2025-07-18" },
  { id: "news:6", name: "New Programs Launching This Fall (mock)", date_posted: "2025-06-20" },
  { id: "news:5", name: "Annual Fundraiser Exceeds Goals (mock)", date_posted: "2025-05-12" },
  { id: "news:4", name: "Community Spotlight: Local Heroes (mock)", date_posted: "2025-04-05" },
  { id: "news:3", name: "Local News #3 (mock)", date_posted: "2025-03-10" },
  { id: "news:2", name: "Local News #2 (mock)", date_posted: "2025-02-15" },
  { id: "news:1", name: "Local News #1 (mock)", date_posted: "2025-01-01" },
];

function buildMockEventIcs(title: string, startDate: string, slug: string, location?: string) {
  const start = String(startDate || "").replace(/-/g, "");
  const safeLocation = String(location || "").replace(/\n/g, " ");

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Easyblocks Local Mock//EN",
    "BEGIN:VEVENT",
    `UID:${slug}@easyblocks.local`,
    "DTSTAMP:20250101T000000Z",
    `DTSTART;VALUE=DATE:${start}`,
    `SUMMARY:${title}`,
    `LOCATION:${safeLocation}`,
    `DESCRIPTION:${title} (local mock event)`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

/**
 * Mock events data for local backend
 */
export const mockEventsData: Record<string, any> = {
  "events:1": {
    item_id: 1,
    C_com_group_item_id: 1,
    item_name: "Tech Conference 2025 (mock)",
    C_com_group_item_name: "Tech Conference 2025 (mock)",
    start_date: "2025-06-15",
    start_time: "09:00:00",
    end_date: "2025-06-17",
    location: "San Francisco Convention Center",
    details: "Join us for the biggest tech conference of the year. Featuring keynotes, workshops, and networking opportunities.",
    page_title: "Tech Conference 2025",
    meta_keywords: "tech,conference,2025,san francisco",
    meta_description: "Annual tech conference in San Francisco",
    slug: "tech-conference-2025",
    calendar_event_data_ics: null,
  },
  "events:2": {
    item_id: 2,
    C_com_group_item_id: 2,
    item_name: "Summer Festival (mock)",
    C_com_group_item_name: "Summer Festival (mock)",
    start_date: "2025-07-20",
    start_time: "11:00:00",
    end_date: "2025-07-20",
    location: "Golden Gate Park",
    details: "A day of music, food, and fun in the sun. Bring your family and friends!",
    page_title: "Summer Festival",
    meta_keywords: "summer,festival,music,park",
    meta_description: "Annual summer festival at Golden Gate Park",
    slug: "summer-festival",
    calendar_event_data_ics: "   ",
  },
  "events:3": {
    item_id: 3,
    C_com_group_item_id: 3,
    item_name: "Product Launch Event (mock)",
    C_com_group_item_name: "Product Launch Event (mock)",
    start_date: "2025-05-10",
    start_time: "14:00:00",
    end_date: "2025-05-10",
    location: "Virtual Event (Online)",
    details: "Be the first to see our new product line. <strong>Register now</strong> for exclusive early access.",
    page_title: "Product Launch Event",
    meta_keywords: "product,launch,virtual,event",
    meta_description: "Virtual product launch event",
    slug: "product-launch",
    calendar_event_data_ics: buildMockEventIcs("Product Launch Event (mock)", "2025-05-10", "product-launch", "Virtual Event (Online)"),
  },
  "events:4": {
    item_id: 4,
    C_com_group_item_id: 4,
    item_name: "Spring Networking Mixer (mock)",
    C_com_group_item_name: "Spring Networking Mixer (mock)",
    start_date: "2025-04-18",
    start_time: "17:30:00",
    end_date: "2025-04-18",
    location: "Downtown Community Center",
    details: "Connect with fellow members and local business leaders at our spring networking event. Light refreshments will be served.",
    page_title: "Spring Networking Mixer",
    meta_keywords: "networking,mixer,spring,business",
    meta_description: "Spring networking event for members",
    slug: "spring-networking-mixer",
    calendar_event_data_ics: "",
  },
  "events:5": {
    item_id: 5,
    C_com_group_item_id: 5,
    item_name: "Annual Charity Golf Tournament (mock)",
    C_com_group_item_name: "Annual Charity Golf Tournament (mock)",
    start_date: "2025-09-05",
    start_time: "08:00:00",
    end_date: "2025-09-05",
    location: "Lakeside Golf Club",
    details: "Join us for our annual charity golf tournament. All proceeds benefit local youth programs. Includes lunch and awards ceremony.",
    page_title: "Annual Charity Golf Tournament",
    meta_keywords: "golf,tournament,charity,fundraiser",
    meta_description: "Charity golf tournament fundraiser",
    slug: "charity-golf-tournament",
    calendar_event_data_ics: buildMockEventIcs("Annual Charity Golf Tournament (mock)", "2025-09-05", "charity-golf-tournament", "Lakeside Golf Club"),
  },
  "events:6": {
    item_id: 6,
    C_com_group_item_id: 6,
    item_name: "Holiday Gala & Awards Dinner (mock)",
    C_com_group_item_name: "Holiday Gala & Awards Dinner (mock)",
    start_date: "2025-12-10",
    start_time: "18:30:00",
    end_date: "2025-12-10",
    location: "Grand Ballroom, Hilton Hotel",
    details: "Celebrate the season at our annual holiday gala. Featuring dinner, dancing, and recognition of outstanding members. Formal attire requested.",
    page_title: "Holiday Gala & Awards Dinner",
    meta_keywords: "holiday,gala,awards,dinner,celebration",
    meta_description: "Annual holiday gala and awards ceremony",
    slug: "holiday-gala-awards",
    calendar_event_data_ics: buildMockEventIcs("Holiday Gala & Awards Dinner (mock)", "2025-12-10", "holiday-gala-awards", "Grand Ballroom, Hilton Hotel"),
  },
  "events:7": {
    item_id: 7,
    C_com_group_item_id: 7,
    item_name: "Youth Sports Camp Registration (mock)",
    C_com_group_item_name: "Youth Sports Camp Registration (mock)",
    start_date: "2025-03-01",
    start_time: "09:00:00",
    end_date: "2025-03-15",
    location: "Community Sports Complex",
    details: "Registration period for our summer youth sports camps. Programs available for ages 6-16 in soccer, basketball, and baseball.",
    page_title: "Youth Sports Camp Registration",
    meta_keywords: "youth,sports,camp,registration,summer",
    meta_description: "Summer youth sports camp registration",
    slug: "youth-sports-camp-registration",
    // Intentionally omitted to mimic live data with no calendar field.
  },
  "events:8": {
    item_id: 8,
    C_com_group_item_id: 8,
    item_name: "Fall Workshop Series: Leadership Skills (mock)",
    C_com_group_item_name: "Fall Workshop Series: Leadership Skills (mock)",
    start_date: "2025-10-12",
    start_time: "19:00:00",
    end_date: "2025-11-16",
    location: "Training Center, Room 201",
    details: "Six-week workshop series focused on developing leadership skills. Sessions every Thursday evening. Limited to 20 participants.",
    page_title: "Fall Workshop Series: Leadership Skills",
    meta_keywords: "workshop,leadership,training,professional development",
    meta_description: "Fall leadership skills workshop series",
    slug: "fall-leadership-workshop",
    calendar_event_data_ics: buildMockEventIcs("Fall Workshop Series: Leadership Skills (mock)", "2025-10-12", "fall-leadership-workshop", "Training Center, Room 201"),
  },
};

/**
 * Mock events search items
 */
export const mockEventsSearchItems = [
  { id: "events:7", item_name: "Youth Sports Camp Registration (mock)", start_date: "2025-03-01" },
  { id: "events:4", item_name: "Spring Networking Mixer (mock)", start_date: "2025-04-18" },
  { id: "events:3", item_name: "Product Launch Event (mock)", start_date: "2025-05-10" },
  { id: "events:1", item_name: "Tech Conference 2025 (mock)", start_date: "2025-06-15" },
  { id: "events:2", item_name: "Summer Festival (mock)", start_date: "2025-07-20" },
  { id: "events:5", item_name: "Annual Charity Golf Tournament (mock)", start_date: "2025-09-05" },
  { id: "events:8", item_name: "Fall Workshop Series: Leadership Skills (mock)", start_date: "2025-10-12" },
  { id: "events:6", item_name: "Holiday Gala & Awards Dinner (mock)", start_date: "2025-12-10" },
];

/**
 * Mock roster search items
 */
const defaultRosterSegmentLabels = {
  s_1: "Standard",
  s_4: "Board Plus",
};

const mockRosterSeedItems = [
  { KD_user_item_id: 101, first_name: "Ana", last_name: "Gomez", user_email: "ana.gomez@example.com", mobile_phone: "555-0110", is_on_board: "Y", is_member: "Y", is_primary_poc: "Y", s_1: "Y" },
  { KD_user_item_id: 102, first_name: "Luis", last_name: "Martinez", user_email: "luis.martinez@example.com", mobile_phone: "555-0111", is_on_board: "N", is_member: "Y", is_editor: "Y", s_1: "Y", s_4: "Y" },
  { KD_user_item_id: 103, first_name: "Maria", last_name: "Perez", user_email: "maria.perez@example.com", mobile_phone: "555-0112", is_on_board: "Y", is_member: "Y", s_4: "Y" },
  { KD_user_item_id: 104, first_name: "James", last_name: "Turner", user_email: "james.turner@example.com", mobile_phone: "555-0113", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 105, first_name: "Priya", last_name: "Shah", user_email: "priya.shah@example.com", mobile_phone: "555-0114", is_on_board: "N", is_member: "Y", is_primary_poc: "Y", s_1: "Y" },
  { KD_user_item_id: 106, first_name: "Derrick", last_name: "Coleman", user_email: "derrick.coleman@example.com", mobile_phone: "555-0115", is_on_board: "N", is_member: "N", s_1: "Y" },
  { KD_user_item_id: 107, first_name: "Heather", last_name: "Nguyen", user_email: "heather.nguyen@example.com", mobile_phone: "555-0116", is_on_board: "Y", is_member: "Y", is_editor: "Y", s_4: "Y" },
  { KD_user_item_id: 108, first_name: "Omar", last_name: "Reyes", user_email: "omar.reyes@example.com", mobile_phone: "555-0117", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 109, first_name: "Claire", last_name: "Bennett", user_email: "claire.bennett@example.com", mobile_phone: "555-0118", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 110, first_name: "Mason", last_name: "Price", user_email: "mason.price@example.com", mobile_phone: "555-0119", is_on_board: "N", is_member: "Y", is_editor: "Y", s_1: "Y" },
  { KD_user_item_id: 111, first_name: "Elena", last_name: "Ramirez", user_email: "elena.ramirez@example.com", mobile_phone: "555-0120", is_on_board: "Y", is_member: "Y", s_4: "Y" },
  { KD_user_item_id: 112, first_name: "Marcus", last_name: "Lee", user_email: "marcus.lee@example.com", mobile_phone: "555-0121", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 113, first_name: "Avery", last_name: "Collins", user_email: "avery.collins@example.com", mobile_phone: "555-0122", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 114, first_name: "Noah", last_name: "Sullivan", user_email: "", mobile_phone: "555-0123", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 115, first_name: "Sofia", last_name: "Morales", user_email: "sofia.morales@example.com", mobile_phone: "", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 116, first_name: "Caleb", last_name: "Brooks", user_email: "caleb.brooks@example.com", mobile_phone: "555-0125", is_on_board: "N", is_member: "N", s_1: "Y" },
  { KD_user_item_id: 117, first_name: "Jasmine", last_name: "Foster", user_email: "jasmine.foster@example.com", mobile_phone: "555-0126", is_on_board: "Y", is_member: "Y", is_primary_poc: "Y", s_4: "Y" },
  { KD_user_item_id: 118, first_name: "Ethan", last_name: "Ward", user_email: "ethan.ward@example.com", mobile_phone: "555-0127", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 119, first_name: "Natalie", last_name: "Hughes", user_email: "natalie.hughes@example.com", mobile_phone: "555-0128", is_on_board: "N", is_member: "Y", is_editor: "Y", s_1: "Y" },
  { KD_user_item_id: 120, first_name: "Victor", last_name: "Sanders", user_email: "victor.sanders@example.com", mobile_phone: "555-0129", is_on_board: "Y", is_member: "Y", s_4: "Y" },
  { KD_user_item_id: 121, first_name: "Lila", last_name: "Patel", user_email: "lila.patel@example.com", mobile_phone: "555-0130", is_on_board: "N", is_member: "Y", s_1: "Y" },
  { KD_user_item_id: 122, first_name: "Gavin", last_name: "Ross", user_email: "gavin.ross@example.com", mobile_phone: "555-0131", is_on_board: "N", is_member: "Y", s_1: "Y" },
];

export const mockRosterSearchItems = mockRosterSeedItems.map((item) => ({
  id: `roster:${item.KD_user_item_id}`,
  ...item,
  segment_labels: defaultRosterSegmentLabels,
}));

export const mockRosterSegments = [
  {
    id: "roster_segment:1",
    item_id: 1,
    key: "s_1",
    label: "Standard",
    item_name: "Standard",
    list_sort: 1,
    C_com_group_item_id: 33,
  },
  {
    id: "roster_segment:2",
    item_id: 2,
    key: "s_4",
    label: "Board Plus",
    item_name: "Board Plus",
    list_sort: 4,
    C_com_group_item_id: 33,
  },
];

export const mockResidentsListItems = [
  {
    id: "resident:101",
    item_id: 101,
    first_name: "Ana",
    last_name: "Gomez",
    address: "101 Lakeview Dr",
    user_email: "ana.gomez@example.com",
    phone_home: "555-2101",
    mobile_phone: "555-0110",
    neighborhood_number: "12",
  },
  {
    id: "resident:102",
    item_id: 102,
    first_name: "Luis",
    last_name: "Martinez",
    address: "202 Sunset Blvd",
    user_email: "luis.martinez@example.com",
    phone_home: "",
    mobile_phone: "555-0111",
    neighborhood_number: "7",
  },
  {
    id: "resident:103",
    item_id: 103,
    first_name: "Maria",
    last_name: "Perez",
    address: "303 Pine Ridge Ct",
    user_email: "maria.perez@example.com",
    phone_home: "555-2103",
    mobile_phone: "555-0112",
    neighborhood_number: "3",
  },
];

export const mockAlbumsSearchItems = [
  { id: "albums:1", item_id: 1, name: "Community Highlights (mock)", item_name: "Community Highlights (mock)", list_sort: "0000000020" },
  { id: "albums:2", item_id: 2, name: "Neighborhood Events (mock)", item_name: "Neighborhood Events (mock)", list_sort: "0000000010" },
];

export const mockAlbumPhotos: Record<string, Array<Record<string, any>>> = {
  "albums:1": [
    { id: "photos:101", item_id: 101, item_name: "Summer potluck in the park", attachment_filename: "summer-potluck.jpg", url: "https://placehold.co/1200x900/0d6efd/fff?text=Summer+Potluck+(4:3)" },
    { id: "photos:102", item_id: 102, item_name: "Clubhouse ribbon cutting", attachment_filename: "ribbon-cutting.jpg", url: "https://placehold.co/1200x900/198754/fff?text=Ribbon+Cutting+(4:3)" },
    { id: "photos:103", item_id: 103, item_name: "Lakeside sunset", attachment_filename: "lakeside-sunset.jpg", url: "https://placehold.co/1200x900/fd7e14/fff?text=Lakeside+Sunset+(4:3)" },
    { id: "photos:104", item_id: 104, item_name: "Annual 5K finish line", attachment_filename: "5k-finish.jpg", url: "https://placehold.co/900x1200/6610f2/fff?text=5K+Finish+(3:4)" },
    { id: "photos:105", item_id: 105, item_name: "Pool grand opening", attachment_filename: "pool-opening.jpg", url: "https://placehold.co/900x1200/20c997/fff?text=Pool+Opening+(3:4)" },
  ],
  "albums:2": [
    { id: "photos:201", item_id: 201, item_name: "Fall festival booths", attachment_filename: "fall-festival.jpg", url: "https://placehold.co/1200x900/dc3545/fff?text=Fall+Festival+(4:3)" },
    { id: "photos:202", item_id: 202, item_name: "Holiday lights contest winner", attachment_filename: "lights-contest.jpg", url: "https://placehold.co/900x1200/0dcaf0/fff?text=Lights+Contest+(3:4)" },
    { id: "photos:203", item_id: 203, item_name: "Neighborhood block party", attachment_filename: "block-party.jpg", url: "https://placehold.co/1200x900/ffc107/000?text=Block+Party+(4:3)" },
    { id: "photos:204", item_id: 204, item_name: "Spring cleanup volunteers", attachment_filename: "spring-cleanup.jpg", url: "https://placehold.co/1200x900/6c757d/fff?text=Spring+Cleanup+(4:3)" },
  ],
};

export function getAlbumLocal(albumId: string): { album: Record<string, any> | null; photos: Array<Record<string, any>> } {
  if (MOCK_EMPTY_ALBUMS) return { album: null, photos: [] };
  const album = mockAlbumsSearchItems.find((it) => String(it.id ?? "") === albumId) ?? null;
  const photos = mockAlbumPhotos[albumId] ?? [];
  return { album, photos };
}

/**
 * Mock private docs categories
 */
export const mockPrivateDocCategories = [
  { id: 1, name: "Policies" },
  { id: 2, name: "Reports" },
  { id: 3, name: "Forms" },
];

function formatMockDate(date: string): string {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  if (!year || !month || !day) return date;
  return `${month}/${day}/${year}`;
}

/**
 * Mock private docs list
 */
export const mockPrivateDocs = [
  // — Policies —
  {
    id: 101,
    name: "Employee Handbook",
    filename: "employee_handbook.pdf",
    url: "/private/employee_handbook.pdf",
    categoryId: 1,
    categoryName: "Policies",
    groupName: "Admin",
    publishDate: "",
    year: 2024,
  },
  {
    id: 104,
    name: "Code of Conduct",
    filename: "code_of_conduct.pdf",
    url: "/private/code_of_conduct.pdf",
    categoryId: 1,
    categoryName: "Policies",
    groupName: "Admin",
    publishDate: formatMockDate("2024-11-01"),
    year: 2024,
  },
  {
    id: 105,
    name: "Privacy Policy",
    filename: "privacy_policy.pdf",
    url: "/private/privacy_policy.pdf",
    categoryId: 1,
    categoryName: "Policies",
    groupName: "Admin",
    publishDate: formatMockDate("2025-01-15"),
    year: 2025,
  },
  {
    id: 106,
    name: "Acceptable Use Policy",
    filename: "acceptable_use_policy.pdf",
    url: "/private/acceptable_use_policy.pdf",
    categoryId: 1,
    categoryName: "Policies",
    groupName: "Admin",
    publishDate: formatMockDate("2024-09-10"),
    year: 2024,
  },
  // — Reports —
  {
    id: 102,
    name: "Quarterly Report Q1, Really long filename to test wrapping. We don't want to truncate titles.",
    filename: "q1_report.pdf",
    url: "/private/q1_report.pdf",
    categoryId: 2,
    categoryName: "Reports",
    groupName: "Admin",
    publishDate: formatMockDate("2025-03-20"),
    year: 2025,
  },
  {
    id: 107,
    name: "Quarterly Report Q2",
    filename: "q2_report.pdf",
    url: "/private/q2_report.pdf",
    categoryId: 2,
    categoryName: "Reports",
    groupName: "Admin",
    publishDate: formatMockDate("2025-06-30"),
    year: 2025,
  },
  {
    id: 108,
    name: "Annual Budget Summary 2025",
    filename: "annual_budget_2025.pdf",
    url: "/private/annual_budget_2025.pdf",
    categoryId: 2,
    categoryName: "Reports",
    groupName: "Admin",
    publishDate: formatMockDate("2025-01-05"),
    year: 2025,
  },
  {
    id: 109,
    name: "Membership Statistics Report",
    filename: "membership_stats.pdf",
    url: "/private/membership_stats.pdf",
    categoryId: 2,
    categoryName: "Reports",
    groupName: "Admin",
    publishDate: formatMockDate("2025-04-12"),
    year: 2025,
  },
  {
    id: 110,
    name: "Facilities Inspection Report",
    filename: "facilities_inspection.pdf",
    url: "/private/facilities_inspection.pdf",
    categoryId: 2,
    categoryName: "Reports",
    groupName: "Admin",
    publishDate: formatMockDate("2025-05-18"),
    year: 2025,
  },
  // — Forms —
  {
    id: 103,
    name: "Incident Form",
    filename: "incident_form.docx",
    url: "/private/incident_form.docx",
    categoryId: 3,
    categoryName: "Forms",
    groupName: "Admin",
    publishDate: formatMockDate("2025-02-12"),
    year: 2025,
  },
  {
    id: 111,
    name: "Membership Application",
    filename: "membership_application.pdf",
    url: "/private/membership_application.pdf",
    categoryId: 3,
    categoryName: "Forms",
    groupName: "Admin",
    publishDate: formatMockDate("2024-08-20"),
    year: 2024,
  },
  {
    id: 112,
    name: "Facility Reservation Request",
    filename: "facility_reservation.pdf",
    url: "/private/facility_reservation.pdf",
    categoryId: 3,
    categoryName: "Forms",
    groupName: "Admin",
    publishDate: formatMockDate("2025-01-30"),
    year: 2025,
  },
  {
    id: 113,
    name: "Volunteer Sign-Up Form",
    filename: "volunteer_signup.pdf",
    url: "/private/volunteer_signup.pdf",
    categoryId: 3,
    categoryName: "Forms",
    groupName: "Admin",
    publishDate: formatMockDate("2025-03-05"),
    year: 2025,
  },
];

function normalizePrivateDoc(
  doc: {
    id?: number;
    item_id?: number;
    name?: string;
    item_name?: string;
    [key: string]: any;
  }
): PrivateDocUploadResponse {
  const itemId = doc.item_id ?? doc.id;
  const itemName = String(doc.item_name ?? doc.name ?? "").trim();

  return {
    ...doc,
    id: itemId,
    item_id: itemId,
    item_name: itemName || undefined,
    description: itemName || undefined,
    name: itemName || doc.name,
  };
}

/**
 * Resolve external data for local backend
 */
export function resolveExternalDataLocal(payload: {
  externals: Record<string, { id: string | null; widgetId: string; type?: string }>;
}): { externalData: ExternalData } {
  const externalData: ExternalData = {};
  for (const [locationKey, ref] of Object.entries(payload.externals ?? {})) {
    if (ref?.widgetId === "news") {
      if (!ref?.id) continue;
      const value = mockNewsData[String(ref.id)];
      if (!value) continue;
      externalData[locationKey] = { type: "news", value } as any;
    } else if (ref?.widgetId === "events") {
      if (!ref?.id) continue;
      const value = mockEventsData[String(ref.id)];
      if (!value) continue;
      externalData[locationKey] = { type: "events", value } as any;
    }
  }
  return { externalData };
}

/**
 * Search external items for local backend
 */
export function searchExternalItemsLocal(payload: {
  source: string;
  q: string;
  limit?: number;
}): { items: Array<Record<string, any>> } {
  if (payload.source === "news") {
    if (MOCK_EMPTY_NEWS) return { items: [] };
    const q = (payload.q ?? "").toLowerCase().trim();
    const filtered = q
      ? mockNewsSearchItems.filter((i) => i.name.toLowerCase().includes(q))
      : mockNewsSearchItems;
    return { items: filtered.slice(0, payload.limit ?? 50) };
  } else if (payload.source === "events") {
    if (MOCK_EMPTY_EVENTS) return { items: [] };
    const q = (payload.q ?? "").toLowerCase().trim();
    const filtered = q
      ? mockEventsSearchItems.filter((i) => (i.C_com_group_item_name ?? "").toLowerCase().includes(q))
      : mockEventsSearchItems;
    return { items: filtered.slice(0, payload.limit ?? 50) };
  } else if (payload.source === "roster") {
    if (MOCK_EMPTY_ROSTER) return { items: [] };
    const q = (payload.q ?? "").toLowerCase().trim();
    const filtered = q
      ? mockRosterSearchItems.filter((i) =>
          `${i.last_name ?? ""} ${i.first_name ?? ""} ${i.user_email ?? ""} ${i.mobile_phone ?? ""}`
            .toLowerCase()
            .includes(q)
        )
      : mockRosterSearchItems;
    return { items: filtered.slice(0, payload.limit ?? 50) };
  } else if (payload.source === "roster_segments") {
    return { items: mockRosterSegments.slice(0, payload.limit ?? 20) };
  } else if (payload.source === "residents") {
    if (MOCK_EMPTY_ROSTER) return { items: [] };
    const q = (payload.q ?? "").toLowerCase().trim();
    const filtered = q
      ? mockResidentsListItems.filter((i) =>
          `${i.last_name ?? ""} ${i.first_name ?? ""} ${i.address ?? ""}`
            .toLowerCase()
            .includes(q)
        )
      : mockResidentsListItems;
    return { items: filtered.slice(0, payload.limit ?? 5000) };
  } else if (payload.source === "albums") {
    if (MOCK_EMPTY_ALBUMS) return { items: [] };
    return { items: mockAlbumsSearchItems.slice(0, payload.limit ?? 50) };
  }
  return { items: [] };
}

/**
 * Upload media for local backend (converts to data URL)
 */
export async function uploadMediaLocal(
    file: File,
    path?: string,
    _uploadContext?: string
): Promise<MediaUploadResponse> {
    const url = await fileToDataUrl(file);
    return { url, name: file.name, size: file.size, mime: file.type, path };
}

/**
 * List private docs for local backend
 */
export function listPrivateDocsLocal(params: {
  limit?: number;
  offset?: number;
  category_id?: number;
  year?: number;
}): PrivateDocListResponse {
  if (MOCK_EMPTY_DOCUMENTS) {
    return {
      items: [],
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    };
  }
  const byCategory = params.category_id
    ? mockPrivateDocs.filter((doc) => doc.categoryId === params.category_id)
    : mockPrivateDocs;
  const filtered = params.year ? byCategory.filter((doc) => doc.year === params.year) : byCategory;
  const limit = params.limit && params.limit > 0 ? params.limit : filtered.length;
  return {
    items: filtered.slice(0, limit).map(normalizePrivateDoc),
    limit: params.limit ?? filtered.length,
    offset: params.offset ?? 0,
  };
}

/**
 * List private doc categories for local backend
 */
export function listPrivateDocCategoriesLocal(): PrivateDocCategoriesResponse {
  return { items: mockPrivateDocCategories };
}

/**
 * Upload private doc for local backend
 */
export async function uploadPrivateDocLocal(params: {
  file: File;
  item_name?: string;
  category_id?: number;
}): Promise<PrivateDocUploadResponse> {
  const { file, item_name, category_id } = params;
  const categoryName = category_id
    ? mockPrivateDocCategories.find((category) => category.id === category_id)?.name ?? ""
    : "";
  return normalizePrivateDoc({
    id: Math.floor(Math.random() * 100000),
    item_name: item_name?.trim() || file.name.replace(/\.[^/.]+$/, ""),
    filename: file.name,
    url: `/private/${file.name}`,
    categoryId: category_id ?? 0,
    categoryName,
    groupName: "",
    publishDate: formatMockDate(new Date().toISOString().slice(0, 10)),
    year: new Date().getFullYear(),
  });
}

/**
 * Mock media file system for local backend testing.
 * Supports folder navigation and basic search filtering.
 */
const mockMediaTree: Record<
  string,
  { folders: { name: string; path: string }[]; files: { name: string; path: string; url: string }[] }
> = {
  "": {
    folders: [
      { name: "Photos", path: "Photos" },
      { name: "Banners", path: "Banners" },
      { name: "Logos", path: "Logos" },
    ],
    files: [
      { name: "hero.jpg", path: "hero.jpg", url: "https://placehold.co/800x400/0d6efd/fff?text=hero.jpg" },
      { name: "background.png", path: "background.png", url: "https://placehold.co/1200x600/198754/fff?text=background.png" },
    ],
  },
  Photos: {
    folders: [
      { name: "Events", path: "Photos/Events" },
      { name: "Headshots", path: "Photos/Headshots" },
    ],
    files: [
      { name: "group-photo.jpg", path: "Photos/group-photo.jpg", url: "https://placehold.co/600x400/6c757d/fff?text=group-photo.jpg" },
      { name: "landscape.jpg", path: "Photos/landscape.jpg", url: "https://placehold.co/800x500/0dcaf0/fff?text=landscape.jpg" },
    ],
  },
  "Photos/Events": {
    folders: [],
    files: [
      { name: "gala-2025.jpg", path: "Photos/Events/gala-2025.jpg", url: "https://placehold.co/600x400/6610f2/fff?text=gala-2025.jpg" },
      { name: "picnic.jpg", path: "Photos/Events/picnic.jpg", url: "https://placehold.co/600x400/20c997/fff?text=picnic.jpg" },
      { name: "meeting.jpg", path: "Photos/Events/meeting.jpg", url: "https://placehold.co/600x400/fd7e14/fff?text=meeting.jpg" },
    ],
  },
  "Photos/Headshots": {
    folders: [],
    files: [
      { name: "jane-doe.jpg", path: "Photos/Headshots/jane-doe.jpg", url: "https://placehold.co/300x300/dc3545/fff?text=jane-doe.jpg" },
      { name: "john-smith.jpg", path: "Photos/Headshots/john-smith.jpg", url: "https://placehold.co/300x300/ffc107/000?text=john-smith.jpg" },
    ],
  },
  Banners: {
    folders: [],
    files: [
      { name: "summer-banner.jpg", path: "Banners/summer-banner.jpg", url: "https://placehold.co/1200x300/198754/fff?text=summer-banner.jpg" },
      { name: "winter-banner.jpg", path: "Banners/winter-banner.jpg", url: "https://placehold.co/1200x300/0dcaf0/fff?text=winter-banner.jpg" },
    ],
  },
  Logos: {
    folders: [],
    files: [
      { name: "logo-dark.png", path: "Logos/logo-dark.png", url: "https://placehold.co/200x200/212529/fff?text=logo-dark.png" },
      { name: "logo-light.png", path: "Logos/logo-light.png", url: "https://placehold.co/200x200/f8f9fa/000?text=logo-light.png" },
    ],
  },
};

export function listMediaLocal(params: { path?: string; q?: string }): {
  path: string;
  folders: { name: string; path: string }[];
  files: { name: string; path: string; url: string }[];
} {
  const currentPath = (params.path ?? "").replace(/^\/+|\/+$/g, "");
  const entry = mockMediaTree[currentPath] ?? { folders: [], files: [] };
  const q = (params.q ?? "").toLowerCase().trim();

  if (!q) {
    return { path: currentPath, folders: entry.folders, files: entry.files };
  }

  // When searching, return matching files from the current folder
  const filteredFiles = entry.files.filter((f) => f.name.toLowerCase().includes(q));
  return { path: currentPath, folders: entry.folders, files: filteredFiles };
}
